/*
 * Last Chance Offer — intercepts the Checkout click and offers a set of
 * discounted products the shopper does not already have.
 *
 * Spec: docs/superpowers/specs/2026-09-16-last-chance-checkout-offer-design.md
 *
 * The governing rule in here is that NOTHING may end up standing between a
 * shopper and checkout. Every failure path — no eligible products, storage
 * unavailable, a failed add, an unexpected exception — falls through to letting
 * the original submit happen.
 */
(function () {
  'use strict';

  var CART_FORM = 'form.cart-form';
  var OFFER_TAG = 'last-chance-offer';

  /* Every control that takes the shopper out of the cart and into a checkout.
     Both live inside form.cart-form, on the cart page and in the drawer.

     `[name="checkout"]`      the theme's own Checkout submit input.
     `.sezzle-checkout-button` Sezzle's "Checkout with…" — an <a> with an EMPTY
                               href and a JS-attached handler, so the same
                               capture-phase stopImmediatePropagation holds it,
                               and proceed()'s bypass re-click releases it. It
                               wraps an <img>, so a click can land on the child
                               and closest() has to walk up to the anchor.

     Deliberately NOT included: Shop Pay, PayPal and the other express wallets.
     Those render inside Shopify-owned iframes, so a click on them never reaches
     this listener anyway, and interfering with a wallet's own flow risks
     breaking payment. Affirm here is messaging ("as low as $32/mo"), not a
     checkout control. */
  var CHECKOUT_CONTROLS = '[name="checkout"], .sezzle-checkout-button';

  /* Hidden line-item property (leading underscore keeps it out of the cart,
     checkout and order displays) that marks a line as having come from this
     modal. The discount Function grants the percentage ONLY to lines carrying
     it, which is what makes "only available here" true: adding the same product
     from its own page produces no property and therefore no last-chance price. */
  var CLAIM_PROPERTY = '_lc';

  /* Products the shopper has had in their cart at any point this visit. Kept so
     that emptying the cart cannot turn an item they already chose into a
     last-chance offer — the "remove it and re-add it cheaper" loop. */
  var SEEN_PRODUCTS_KEY = 'mg-last-chance-seen-products';

  var CART_LOOKUP_TIMEOUT = 1500;

  /* Closing the modal must NOT burn the offer — a misclick on the × should not
     cost the shopper a discount. So "seen" is recorded when they actually go
     through to checkout, not when the modal appears, and closing simply re-arms
     it for the next Checkout click.

     The case that leaves is a shopper going back and forth editing their cart,
     who would meet the modal on every attempt. That is answered by an explicit
     opt-out control rather than by a guessed cap on showings: the shopper says
     when they are done with it, and nobody has to predict the number.

     localStorage, not session: the control promises "don't show this again", so
     honouring it only until the tab closes would be a lie. Thirty days, after
     which a genuinely new offer can reach them again. */
  var OPT_OUT_KEY = 'mg-last-chance-opted-out';
  var OPT_OUT_DAYS = 30;

  function hasOptedOut() {
    try {
      var at = Number(window.localStorage.getItem(OPT_OUT_KEY));
      if (!at) return false;
      var age = Date.now() - at;
      if (age > OPT_OUT_DAYS * 24 * 60 * 60 * 1000) {
        window.localStorage.removeItem(OPT_OUT_KEY);
        return false;
      }
      return true;
    } catch (e) {
      return false;
    }
  }

  function setOptedOut() {
    try {
      window.localStorage.setItem(OPT_OUT_KEY, String(Date.now()));
    } catch (e) {
      /* Storage unavailable: the opt-out lasts this page only. Better than
         refusing to close. */
    }
  }

  function readSeenProducts() {
    try {
      var raw = window.sessionStorage.getItem(SEEN_PRODUCTS_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  }

  function rememberProducts(ids) {
    try {
      var merged = readSeenProducts();
      ids.forEach(function (id) {
        if (merged.indexOf(id) === -1) merged.push(id);
      });
      window.sessionStorage.setItem(SEEN_PRODUCTS_KEY, JSON.stringify(merged));
      return merged;
    } catch (e) {
      return ids;
    }
  }

  /* A cart lookup that cannot hang the checkout button. Whatever happens, the
     caller gets an answer inside CART_LOOKUP_TIMEOUT. */
  function fetchCart() {
    return new Promise(function (resolve) {
      var settled = false;
      var done = function (value) {
        if (settled) return;
        settled = true;
        resolve(value);
      };

      window.setTimeout(function () {
        done(null);
      }, CART_LOOKUP_TIMEOUT);

      /* try/catch, not just .catch(): the store's cart app (Cartbot) replaces
         window.fetch with its own wrapper, and that wrapper can throw
         SYNCHRONOUSLY — observed live as
         "TypeError: Cannot read properties of null (reading 'items')".
         A synchronous throw inside a Promise executor REJECTS the promise
         before the timeout above can resolve it, so .catch() on the chain
         never runs and the caller waits forever. */
      try {
        fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) {
            return r.json();
          })
          .then(done)
          .catch(function () {
            done(null);
          });
      } catch (e) {
        done(null);
      }
    });
  }

  /* Guarded because Safari private browsing throws on sessionStorage access
     rather than returning null. A throw here must read as "already shown", so
     the shopper is never blocked by a storage failure. */
  function hasSeen(key) {
    try {
      return window.sessionStorage.getItem(key) === '1';
    } catch (e) {
      return true;
    }
  }

  function markSeen(key) {
    try {
      window.sessionStorage.setItem(key, '1');
    } catch (e) {
      /* no-op: worst case the popup shows again, which is survivable */
    }
  }

  /* `addon: yes` keeps these lines consistent with every other add-on the theme
     creates (cart-upsell.liquid reads it to pick a "last non-add-on item").
     CLAIM_PROPERTY is what the discount Function looks for. */
  function claimProperties() {
    var props = { addon: 'yes' };
    props[CLAIM_PROPERTY] = '1';
    return props;
  }

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      var format = (window.cart_upsell && window.cart_upsell.money_format) || '${{amount}}';
      return window.Shopify.formatMoney(cents, format);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /* ---------------------------------------------------------------------- */

  class LastChanceItem extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      var self = this;
      var open = function (event) {
        event.preventDefault();
        event.stopPropagation();
        self.dispatchEvent(new CustomEvent('last-chance:add-requested', { bubbles: true }));
      };

      /* Both the photo and the + add. The + is no longer inside the image, so
         it needs binding in its own right. */
      var media = this.querySelector('.last-chance__card-media');
      if (media) media.addEventListener('click', open);

      var addBtn = this.querySelector('.last-chance__card-add');
      if (addBtn) addBtn.addEventListener('click', open);
    }

    get productId() {
      return this.dataset.productId;
    }

    get variantId() {
      return this.dataset.variantId;
    }

    get isMultiVariant() {
      return this.dataset.multiVariant === 'true';
    }

    get discountAmount() {
      return Number(this.dataset.discountAmount) || 0;
    }

    get handle() {
      return this.dataset.productHandle || '';
    }

    /* 1-based index among the cards actually on offer, so listPosition means the
       same thing here as it does in the two carousels. */
    get position() {
      var siblings = Array.prototype.slice.call(
        this.parentNode ? this.parentNode.querySelectorAll('last-chance-item') : []
      );
      return siblings.indexOf(this) + 1;
    }

    trackFields() {
      return {
        surface: (window.mgUpsell && window.mgUpsell.SURFACE.LASTCHANCE) || 'lastchance',
        handle: this.handle,
        position: this.position
      };
    }

    /* The trimmed product payload is only rendered for multi-variant products —
       single-variant ones never need the quick view, and inlining full product
       JSON for every offer product on every page of the store is weight nobody
       asked for. */
    get productData() {
      if (this._productData !== undefined) return this._productData;
      var script = this.querySelector('.last-chance__card-data');
      try {
        this._productData = script ? JSON.parse(script.textContent) : null;
      } catch (e) {
        this._productData = null;
      }
      return this._productData;
    }

    setStatus(message, isError) {
      var el = this.querySelector('.last-chance__card-status');
      if (!el) return;
      el.textContent = message || '';
      el.classList.toggle('is-error', !!isError);
    }

    markAdded() {
      this.classList.remove('is-adding');
      this.classList.add('is-added');
      this.setStatus('Added to your cart', false);
    }
  }

  customElements.define('last-chance-item', LastChanceItem);

  /* ---------------------------------------------------------------------- */

  class LastChanceOffer extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      var self = this;

      this.addEventListener('last-chance:add-requested', function (event) {
        var card = event.target.closest('last-chance-item');
        if (card) self.requestAdd(card);
      });

      this.querySelectorAll('[data-last-chance-dismiss]').forEach(function (el) {
        el.addEventListener('click', function () {
          self.close();
        });
      });

      var continueBtn = this.querySelector('.last-chance__continue');
      if (continueBtn) {
        continueBtn.addEventListener('click', function () {
          self.proceed();
        });
      }

      /* Closes and suppresses, but does NOT go to checkout — the shopper may
         have opened this while still editing their cart, and sending them to
         pay because they said "not interested" would be its own small betrayal. */
      var optOut = this.querySelector('.last-chance__optout');
      if (optOut) {
        optOut.addEventListener('click', function () {
          setOptedOut();
          self.close();
        });
      }

      this.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
          event.stopPropagation();
          self.close();
        } else if (event.key === 'Tab') {
          self.trapFocus(event);
        }
      });

      /* The quick view is shared with the cart drawer, so a completed add there
         may or may not belong to this modal. It echoes back the product id it
         acted on; anything we do not recognise is ignored. */
      document.addEventListener('quickview:added', function (event) {
        var id = event.detail && event.detail.productId;
        var card = id && self.querySelector('last-chance-item[data-product-id="' + id + '"]');
        if (!card) return;

        if (window.mgUpsell) {
          window.mgUpsell.add(card.trackFields(), {
            pct: Number(self.dataset.pct) || 0,
            path: 'quickview'
          });
        }

        card.markAdded();
        self.retire(card);
        self.refreshSubtotal();
      });

      document.addEventListener('quickview:add-failed', function (event) {
        var id = event.detail && event.detail.productId;
        var card = id && self.querySelector('last-chance-item[data-product-id="' + id + '"]');
        if (!card) return;
        card.classList.remove('is-adding');
        card.setStatus('Could not be added', true);
        self.showError(event.detail && event.detail.message);
      });
    }

    get sessionKey() {
      return this.dataset.sessionKey || 'mg-last-chance';
    }

    hasOffers() {
      return this.querySelectorAll('last-chance-item:not(.is-added):not([hidden])').length > 0;
    }

    /* ------------------------------------------------------------------ */

    /* Liquid filters the cards against the cart as it stood when the PAGE was
       rendered. That is stale by the time anyone clicks Checkout: an item added
       since — from the PDP add-ons carousel, the drawer's own upsell row, a
       quick-add — would still be sitting in this modal at a better price than
       the shopper just agreed to pay for it. So the live cart is the authority,
       and it is checked here, at the moment of opening. */
    openFor(form, submitter) {
      this._form = form;
      this._submitter = submitter;
      this.prepare();
    }

    prepare() {
      var self = this;

      var render = function (cart) {
        var excluded = cart && cart.items
          ? rememberProducts(cart.items.map(function (i) { return i.product_id; }))
          : readSeenProducts();

        var visible = 0;
        self.querySelectorAll('last-chance-item').forEach(function (card) {
          var drop = excluded.indexOf(Number(card.productId)) !== -1;
          card.hidden = drop;
          if (!drop) visible += 1;
        });

        /* Everything on offer is already theirs. Showing an empty modal, or one
           full of things they cannot use, is worse than not showing it — so get
           out of the way and let the checkout they asked for happen.

           recordSeen:false because this is not the shopper using the offer. The
           anti-gaming memory (SEEN_PRODUCTS_KEY) can empty the list after a few
           adds and removals, and burning the session flag here meant the pop-up
           never returned for the rest of the visit even once the cart changed
           and products became eligible again. Leave it armed. */
        if (visible === 0) {
          self.proceed({ recordSeen: false });
          return;
        }

        self.show(cart);
      };

      /* The click that got us here has already been preventDefault()ed and
         stopImmediatePropagation()ed, so if this path dies the shopper is left
         with a Checkout button that does NOTHING — strictly worse than never
         having intercepted. Every failure must still render: with no cart we
         fall back to the session's remembered products, which is the same
         answer a timed-out lookup gives. */
      try {
        fetchCart()
          .then(render)
          .catch(function () {
            render(null);
          });
      } catch (e) {
        render(null);
      }
    }

    show(cart) {
      this.hidden = false;
      this.classList.add('is-open');
      document.body.style.overflow = 'hidden';

      if (cart) this.renderSubtotal(cart);
      else this.refreshSubtotal();

      var dialog = this.querySelector('.last-chance__dialog');
      if (dialog) {
        dialog.setAttribute('tabindex', '-1');
        dialog.focus();
      }

      /* Impressions start only now. Observing while the modal was still hidden
         would credit every card the moment the page loaded, which is exactly the
         flattery the shared observer exists to avoid. */
      if (window.mgUpsell) {
        this.querySelectorAll('last-chance-item:not([hidden])').forEach(function (card) {
          window.mgUpsell.observe(card, card.trackFields());
        });
      }
    }

    close() {
      this.hidden = true;
      this.classList.remove('is-open');
      document.body.style.overflow = '';
      this.hideError();

      /* Send focus back where it came from, so a keyboard user is not dropped
         at the top of the document. The second click passes straight through
         now that the session flag is set. */
      if (this._submitter && typeof this._submitter.focus === 'function') {
        this._submitter.focus();
      }
    }

    /* Re-fires the original submit. It must be submitter.click(), NOT
       form.submit(): form.submit() omits the submitter's name/value, so the
       `checkout` parameter never reaches Shopify and the shopper lands back on
       the cart page instead of checkout. */
    /* `{ recordSeen: false }` is for the one case that is NOT a shopper using
       the offer: the modal had nothing eligible to show, so it stepped out of
       the way. Recording that as "seen" conflated two different things — "they
       consumed the offer" and "there was nothing to consume" — and the second
       one is temporary, reversible the moment the cart changes. It disarmed the
       pop-up for the rest of the visit even after every product became eligible
       again. Reproduced on live 2026-09-21: seeding the seen-products list made
       the next Checkout click skip silently AND set the session flag. */
    proceed(options) {
      this.hidden = true;
      this.classList.remove('is-open');
      document.body.style.overflow = '';

      /* Recorded for a real pass through the offer and nothing else. The
         shopper has been through it and is on their way to pay; meeting it
         again if they come back would be nagging. Neither closing the modal
         nor an empty skip reaches this. */
      if (!options || options.recordSeen !== false) {
        markSeen(this.sessionKey);
      }

      window.__lastChanceBypass = true;

      if (this._submitter && typeof this._submitter.click === 'function') {
        this._submitter.click();
        return;
      }

      if (this._form) {
        var hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'checkout';
        hidden.value = 'Checkout';
        this._form.appendChild(hidden);
        this._form.submit();
        return;
      }

      window.location.href = '/checkout';
    }

    trapFocus(event) {
      var focusables = this.querySelectorAll(
        'button:not([disabled]), [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;

      var first = focusables[0];
      var last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* ------------------------------------------------------------------ */

    requestAdd(card) {
      if (card.classList.contains('is-added') || card.classList.contains('is-adding')) return;

      this.hideError();

      if (window.mgUpsell) {
        window.mgUpsell.click(card.trackFields(), { pct: Number(this.dataset.pct) || 0 });
      }

      if (card.isMultiVariant) {
        var product = card.productData;
        if (!product) {
          this.showError('Sorry, this item could not be opened. Please try it from its product page.');
          return;
        }

        /* The discount is passed in the event rather than looked up from the
           DOM. The quick view's own lookup finds the FIRST matching element by
           product id, which on the cart page may be the drawer's upsell card
           carrying a different percentage — it would price this item at the
           add-on rate instead of the last-chance rate. */
        document.dispatchEvent(
          new CustomEvent('cart-upsell:open-quickview', {
            bubbles: true,
            detail: {
              product: product,
              discountAmount: card.discountAmount,
              badge: (card.querySelector('.last-chance__card-badge') || {}).innerHTML || '',
              source: 'last-chance',
              /* Carries the claim through the shared quick view, so a
                 last-chance add is marked and an ordinary drawer upsell add
                 is not. */
              properties: claimProperties()
            }
          })
        );
        return;
      }

      this.addVariant(card, card.variantId);
    }

    addVariant(card, variantId) {
      var self = this;
      card.classList.add('is-adding');
      card.setStatus('Adding…', false);

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1, properties: claimProperties() }]
        })
      })
        .then(function (response) {
          return response.json();
        })
        .then(function (data) {
          /* /cart/add.js answers with {status: 422, description} on rejection.
             The drawer's own handler swallows this silently, which is how a
             sold-out add-on used to stop the whole cart with nothing on screen.
             Surface it here instead. */
          if (data && data.status) {
            card.classList.remove('is-adding');
            card.setStatus('Unavailable', true);
            self.showError(data.description || data.message || 'Sorry, that could not be added to your cart.');
            return;
          }

          if (window.mgUpsell) {
            window.mgUpsell.add(card.trackFields(), {
              pct: Number(self.dataset.pct) || 0,
              variant_id: String(variantId),
              path: 'direct'
            });
          }

          card.markAdded();
          self.retire(card);
          self.refreshSubtotal();
          document.dispatchEvent(new CustomEvent('cart:refresh-drawer', { bubbles: true }));
        })
        .catch(function () {
          card.classList.remove('is-adding');
          card.setStatus('Try again', true);
          self.showError('Sorry, something went wrong adding that. You can still continue to checkout.');
        });
    }

    /* ------------------------------------------------------------------ */

    refreshSubtotal() {
      var self = this;
      fetchCart().then(function (cart) {
        if (cart) self.renderSubtotal(cart);
      });
    }

    renderSubtotal(cart) {
      var wrap = this.querySelector('.last-chance__subtotal');
      if (!wrap || !cart || typeof cart.total_price !== 'number') return;

      var now = this.querySelector('.last-chance__subtotal-now');
      var was = this.querySelector('.last-chance__subtotal-was');

      /* innerHTML, not textContent: shop.money_format wraps the amount in
         <span class="money">, which the drawer's own CSS then appends the
         currency code to. Assigning it as text prints the markup. */
      if (now) now.innerHTML = formatMoney(cart.total_price);

      if (was) {
        var showWas = cart.original_total_price > cart.total_price;
        was.hidden = !showWas;
        if (showWas) was.innerHTML = formatMoney(cart.original_total_price);
      }

      wrap.hidden = false;
    }

    /* Once added, a card is retired from the row. It lingers briefly so the
       shopper sees the tick and knows it landed — vanishing on click reads as
       "did that work?" — and then it goes, because an item they now own has no
       business sitting in an offer. */
    retire(card) {
      var self = this;
      window.setTimeout(function () {
        card.hidden = true;
        if (!self.hasOffers()) {
          var empty = self.querySelector('.last-chance__empty');
          if (empty) empty.hidden = false;
        }
      }, 1800);
    }

    showError(message) {
      var el = this.querySelector('.last-chance__error');
      if (!el) return;
      el.textContent = message || 'Sorry, something went wrong.';
      el.hidden = false;
    }

    hideError() {
      var el = this.querySelector('.last-chance__error');
      if (!el) return;
      el.hidden = true;
      el.textContent = '';
    }
  }

  customElements.define('last-chance-offer', LastChanceOffer);

  /* ---------------------------------------------------------------------- */

  /* Keep the offer element in step with the cart.
   *
   * snippets/last-chance-offer.liquid decides what to render from the cart as
   * it stood when the PAGE was built. A shopper who arrives with an empty cart
   * — which is every first visit and every incognito session — gets a page with
   * NO <last-chance-offer> in it at all, because no cart rule matched at render
   * time. Adding a desk or a chair after that changes the cart but not the
   * markup: CartDrawer.refresh() rewrites .cart-drawer__items and friends and
   * never touches this element. offerFor() then finds nothing, and the Checkout
   * click goes straight through to payment.
   *
   * Measured on live 2026-09-24: a collection page loaded with an EMPTY cart
   * contains zero occurrences of `last-chance-offer`; the same URL loaded with
   * a chair already in the cart contains two. That is the whole bug — the
   * feature was only ever visible to someone whose cart already qualified
   * before the page rendered, which is why it worked in seeded tests and never
   * for a real shopper.
   *
   * Re-render from the same section endpoint the drawer already uses, so all
   * the rule / collection / availability logic stays in Liquid — the only place
   * that can see which collections a cart's products belong to. */
  var syncTimer = null;

  function placeOffer(node) {
    /* Mirror where Liquid puts it: inside the cart-drawer section, immediately
       after <cart-drawer> so it survives the drawer's own innerHTML rewrites. */
    var drawer = document.querySelector('cart-drawer');
    if (drawer && drawer.parentNode) {
      drawer.parentNode.insertBefore(node, drawer.nextSibling);
      return;
    }
    var section = document.querySelector('#shopify-section-cart-drawer');
    (section || document.body).appendChild(node);
  }

  function syncOffer() {
    var current = document.querySelector(OFFER_TAG);

    /* Never pull the rug out from under a modal the shopper is looking at. */
    if (current && !current.hidden) return;

    var request;
    try {
      request = fetch('/?section_id=cart-drawer', { headers: { Accept: 'text/html' } });
    } catch (e) {
      /* A wrapped fetch can throw synchronously — same hazard as fetchCart(). */
      return;
    }

    request
      .then(function (r) {
        return r.text();
      })
      .then(function (html) {
        var fresh = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector(OFFER_TAG);
        var live = document.querySelector(OFFER_TAG);

        /* Opened while we were fetching — leave it alone. */
        if (live && !live.hidden) return;

        if (!fresh) {
          /* The cart no longer matches any rule: take the stale offer away
             rather than leave one that would promise the wrong products. */
          if (live && live.parentNode) live.parentNode.removeChild(live);
          return;
        }

        var node = document.importNode(fresh, true);
        if (live && live.parentNode) {
          live.parentNode.replaceChild(node, live);
        } else {
          placeOffer(node);
        }
      })
      .catch(function () {
        /* Leave whatever is already on the page. Worst case the offer is a
           beat out of date, which prepare()'s live cart check then corrects. */
      });
  }

  /* Debounced: adding from the drawer fires several of these in a row. */
  function scheduleSync() {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(syncOffer, 250);
  }

  document.addEventListener('cart:refresh-drawer', scheduleSync);
  document.addEventListener('cart:open-drawer', scheduleSync);

  /* ---------------------------------------------------------------------- */

  /* Returns the offer element when this checkout attempt should be intercepted,
     or null to let it through. Null is the safe answer and every uncertain case
     returns it. */
  function offerFor(submitter) {
    /* matches() rather than `name === 'checkout'`, so Sezzle's anchor qualifies
       too. An <a> has no .form property, hence the closest('form') fallback
       just below — that was already here for safety and is now load-bearing. */
    if (!submitter || typeof submitter.matches !== 'function') return null;
    if (!submitter.matches(CHECKOUT_CONTROLS)) return null;

    var form = submitter.form || submitter.closest('form');
    if (!form || typeof form.matches !== 'function' || !form.matches(CART_FORM)) return null;

    /* The terms-and-conditions gate marks the button rather than the event. */
    if (submitter.classList.contains('disagree_conditions')) return null;

    var offer = document.querySelector(OFFER_TAG);
    if (!offer || typeof offer.hasOffers !== 'function' || !offer.hasOffers()) return null;
    if (offer.dataset.designMode === 'true') return null;

    /* Already went through to checkout once this session — don't nag. */
    if (hasSeen(offer.sessionKey)) return null;

    /* They asked not to see it again. */
    if (hasOptedOut()) return null;

    return offer;
  }

  /* Capture phase on click, NOT the submit event.
   *
   * The store has a cart app (it stamps `cartbot-checkout` on the button) that
   * builds its own checkout URL and navigates to it on click. It never submits
   * the form, so preventing the submit stopped nothing — the modal opened and
   * the shopper was sent to checkout a couple of seconds later anyway. Verified
   * on the preview theme before this was changed.
   *
   * stopImmediatePropagation is what actually holds the shopper on the page:
   * the app's own click handler must not run. On the way back out, proceed()
   * sets the bypass flag and re-clicks, so that handler runs normally and the
   * app still gets to build its checkout.
   *
   * WINDOW, not document. A click captures window -> document -> ... -> target,
   * so a capture listener on window runs before ANY capture listener on
   * document, whatever order they registered in — registration order only
   * breaks ties within the same node. On document we could be beaten by the
   * cart app (its bots apply late, well after this file parses, and it stamps
   * `cartbot-checkout` on the button when they do), and being beaten means the
   * shopper is navigated away before the offer is ever consulted. On window we
   * are first in the capture path, and first to register there too.
   *
   * assets/component-product-addons.js binds its add-to-cart handler the same
   * way for the same reason — see the comment on its listener.
   */
  window.addEventListener(
    'click',
    function (event) {
      if (window.__lastChanceBypass) {
        window.__lastChanceBypass = false;
        return;
      }

      var submitter = event.target && event.target.closest
        ? event.target.closest(CHECKOUT_CONTROLS)
        : null;

      var offer = offerFor(submitter);
      if (!offer) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      offer.openFor(submitter.form || submitter.closest('form'), submitter);
    },
    true
  );

  /* Keyboard submits (Enter in a cart field) never fire a click on the button,
     so the submit event is still covered. Bubble phase here, so anything that
     legitimately blocks the submit first is respected. */
  document.addEventListener('submit', function (event) {
    if (window.__lastChanceBypass) {
      window.__lastChanceBypass = false;
      return;
    }

    if (event.defaultPrevented) return;

    var form = event.target;
    if (!form || typeof form.matches !== 'function' || !form.matches(CART_FORM)) return;

    var submitter = event.submitter || form.querySelector('input[name="checkout"], button[name="checkout"]');
    var offer = offerFor(submitter);
    if (!offer) return;

    event.preventDefault();
    offer.openFor(form, submitter);
  });
})();
